
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface UserCropPlan {
  id: string;
  commodity: string | null;
  variety: string | null;
  group_name: string | null;
  season: string | null;
  year: number;
  is_primary: boolean;
}

export const useCropPlanByUserId = (userId: string | null) => {
  const [userCropPlans, setUserCropPlans] = useState<UserCropPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setUserCropPlans([]);
      setLoading(false);
      setError(null);
      return;
    }

    const fetchUserCropPlans = async () => {
      try {
        setLoading(true);
        setError(null);

        // First get the profile ID for this user
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', userId)
          .maybeSingle();

        if (profileError) {
          throw new Error('Profile fetch error: ' + profileError.message);
        }

        if (!profileData) {
          setUserCropPlans([]);
          return;
        }

        // Fetch crop plans for this profile
        const { data: cropPlans, error: cropError } = await supabase
          .from('user_crop_categories')
          .select('*')
          .eq('user_id', profileData.id)
          .order('created_at', { ascending: false });

        if (cropError) {
          throw new Error('Crop plans fetch error: ' + cropError.message);
        }

        setUserCropPlans(cropPlans || []);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (msg.toLowerCase().includes('permission denied') || msg.includes('42501') || msg.includes('401') || msg.includes('403')) {
          console.warn('Non-fatal: permission issue fetching crop plans, showing profile without crops.');
          setUserCropPlans([]);
          setError(null);
        } else {
          console.error('Error fetching crop plans:', err);
          setError(msg || 'Unknown error occurred');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserCropPlans();
  }, [userId]);

  return {
    userCropPlans,
    loading,
    error,
  };
};
